import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './mso.css';

import { getMsoData, submitMsoSignoff, regenerateMso } from '../../lib/api';

/**
 * Manager Sign Off (MSO) tab.
 *
 * Compares Screen Archive panels against the Tech Report site list, grouped by
 * modality. A reviewer can override any individual check; the automated verdict
 * is preserved alongside the override so the record shows both.
 *
 * Props:
 *   caseId    string  - PID / case identifier
 *   isAdmin   boolean - enables sign-off and override controls
 *   onChange  func    - optional, fired after a successful sign-off
 */

const VERDICTS = {
  matched: { label: 'Present', icon: '✓', cls: 'ok' },
  partial: { label: 'Partial', icon: '!', cls: 'warn' },
  unmatched: { label: 'Missing', icon: '✗', cls: 'bad' },
};

const OVERRIDE_CHOICES = [
  { value: '', label: 'Accept system verdict' },
  { value: 'matched', label: 'Mark present' },
  { value: 'partial', label: 'Mark partial' },
  { value: 'unmatched', label: 'Mark missing' },
  { value: 'na', label: 'Not applicable' },
];

const SCORE_WEIGHT = { matched: 1, partial: 0.5, unmatched: 0 };

export default function MSOTab({ caseId, isAdmin = false, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [signoffPending, setSignoffPending] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [comments, setComments] = useState('');
  const [overrides, setOverrides] = useState({});
  const [openGroups, setOpenGroups] = useState({});

  // quiet: refresh in place. A background poll must not flip the whole tab
  // back to "Loading comparison", because that replaces the in-progress panel
  // it is trying to refresh.
  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const result = await getMsoData(caseId);
      setData(result);
      setComments(result?.signoff?.comments || '');
      setOverrides(result?.signoff?.overrides || {});
      const initial = {};
      (result?.comparison?.modality_groups || []).forEach(g => {
        initial[g.modality] = g.default_expanded;
      });
      setOpenGroups(initial);
    } catch (e) {
      setError(e.message || 'Failed to load MSO data');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // While a comparison is running, poll until it settles.
  useEffect(() => {
    if (data?.status !== 'running') return undefined;
    const timer = setInterval(() => load(true), 8000);
    return () => clearInterval(timer);
  }, [data?.status, load]);

  const setOverride = useCallback((itemId, patch) => {
    setOverrides(prev => {
      const next = { ...prev };
      const current = next[itemId] || {};
      const merged = { ...current, ...patch };
      if (!merged.verdict && !merged.note) delete next[itemId];
      else next[itemId] = merged;
      return next;
    });
  }, []);

  const groups = data?.comparison?.modality_groups || [];

  const effective = useMemo(() => {
    let auto = 0, autoTotal = 0, adj = 0, adjTotal = 0, findings = 0;
    groups.forEach(g => {
      [...(g.matched_items || []), ...(g.unmatched_items || [])].forEach(it => {
        const ov = overrides[it.id];
        autoTotal += 1;
        auto += SCORE_WEIGHT[it.match_status] ?? 0;
        if (ov?.verdict === 'na') return;
        const verdict = ov?.verdict || it.match_status;
        adjTotal += 1;
        adj += SCORE_WEIGHT[verdict] ?? 0;
        if (verdict !== 'matched') findings += 1;
      });
    });
    return {
      auto: autoTotal ? Math.round((1000 * auto) / autoTotal) / 10 : null,
      adjusted: adjTotal ? Math.round((1000 * adj) / adjTotal) / 10 : null,
      findings,
      overrideCount: Object.keys(overrides).filter(k => overrides[k]?.verdict).length,
    };
  }, [groups, overrides]);

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true);
    setError(null);
    try {
      await regenerateMso(caseId);
      setData(prev => ({ ...(prev || {}), status: 'running',
                         headline: 'Comparison in progress' }));
      setTimeout(() => load(true), 5000);
    } catch (e) {
      setError(e.message || 'Regenerate failed');
      setRegenerating(false);
      return;
    }
    setRegenerating(false);
  }, [caseId, load]);

  const handleSignoff = useCallback(async (decision) => {
    if (!isAdmin || signoffPending) return;
    setSignoffPending(true);
    try {
      await submitMsoSignoff(caseId, {
        decision,
        comments,
        overrides,
        version: data?.version,
      });
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Sign-off failed');
    } finally {
      setSignoffPending(false);
    }
  }, [isAdmin, signoffPending, caseId, comments, overrides, data, load, onChange]);

  if (loading) return <div className="mso-state">Loading comparison…</div>;
  if (error) return <div className="mso-state mso-error">{error}</div>;
  if (data?.status === 'running') {
    return (
      <div className="mso-state mso-running">
        <div className="mso-spinner" />
        <p className="mso-running-head">Comparison in progress</p>
        <p className="mso-running-note">
          Reading the Screen Archive and matching it against the Tech Report.
          This usually takes two to five minutes. You can leave this page and
          come back.
        </p>
      </div>
    );
  }
  if (data?.status === 'unavailable') {
    return <MissingSources data={data} onRetry={() => load()} />;
  }
  if (!data || data.status === 'none') {
    return (
      <div className="mso-state">
        <p>No comparison has been run for this case.</p>
        <button className="mso-btn primary" onClick={handleRegenerate} disabled={regenerating}>
          {regenerating ? 'Starting…' : 'Run comparison'}
        </button>
      </div>
    );
  }
  if (data.status === 'error') {
    return (
      <div className="mso-state mso-error">
        <p>{data.headline || 'The comparison failed.'}</p>
        <button className="mso-btn" onClick={handleRegenerate}>Retry</button>
      </div>
    );
  }

  const c = data.comparison || {};
  const signed = data.status === 'approved' || data.status === 'rejected';

  const visibleGroups = groups.filter(
    g => g.total_count > 0 || g.usmon_sites?.length > 0
      || g.report_only_items?.length > 0);

  // Pooled across groups so the section header agrees with the case headline
  // rather than being a second, differently-derived number.
  const siteScore = (() => {
    let matched = 0, total = 0;
    visibleGroups.forEach(g => {
      matched += g.matched_count || 0;
      total += (g.matched_count || 0) + (g.discrepancy_count || 0);
    });
    if (!total) return null;
    const pct = (matched / total) * 100;
    return {
      pct: Math.round(pct * 10) / 10,
      band: pct >= 95 ? 'ok' : pct >= 80 ? 'warn' : 'bad',
    };
  })();

  return (
    <div className="mso-root">
      <Header
        status={data.status}
        comparison={c}
        effective={effective}
        signoff={data.signoff}
        onRegenerate={handleRegenerate}
        regenerating={regenerating}
        canRegenerate={isAdmin && !signed}
      />

      <Diagnostics diagnostics={c.diagnostics} />
      <ReportNote report={c.report} />

      <SectionHead
        title="Modalities"
        sub="USMON sites against screenshots and the Tech Report"
        count={`${visibleGroups.length} ${visibleGroups.length === 1 ? 'modality' : 'modalities'}`}
        score={siteScore == null ? null : siteScore.pct}
        band={siteScore?.band}
      />

      {visibleGroups.map(g => (
        <ModalityGroup
          key={g.modality}
          group={g}
          open={!!openGroups[g.modality]}
          onToggle={() => setOpenGroups(p => ({ ...p, [g.modality]: !p[g.modality] }))}
          overrides={overrides}
          onOverride={setOverride}
          onZoom={setZoom}
          editable={isAdmin && !signed}
        />
      ))}

      <TimeChecks checks={c.time_checks} />

      {isAdmin && !signed && (
        <SignoffPanel
          comments={comments}
          setComments={setComments}
          effective={effective}
          onApprove={() => handleSignoff('approved')}
          onReject={() => handleSignoff('rejected')}
          pending={signoffPending}
        />
      )}

      {zoom && <Lightbox item={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

/* -------------------------------------------------------- missing sources */

function MissingSources({ data, onRetry }) {
  const sources = data?.prerequisites?.sources || {};
  const entries = Object.entries(sources);

  return (
    <div className="mso-state mso-unavailable">
      <p className="mso-unavailable-head">
        Manager Sign Off needs both source documents before it can run.
      </p>
      <ul className="mso-source-list">
        {entries.map(([label, info]) => (
          <li key={label} className={info.present ? 'present' : 'absent'}>
            <span className="mso-source-icon">{info.present ? '✓' : '○'}</span>
            <span className="mso-source-name">{label}</span>
            <span className="mso-source-code">{info.code}</span>
            <span className="mso-source-state">
              {info.present ? 'uploaded' : 'not uploaded'}
            </span>
          </li>
        ))}
      </ul>
      <p className="mso-unavailable-note">
        Upload the missing document to this case, then check again.
      </p>
      <button className="mso-btn" onClick={onRetry}>Check again</button>
    </div>
  );
}

/* ------------------------------------------------------------------ header */

function Header({ status, comparison, effective, signoff, onRegenerate, regenerating, canRegenerate }) {
  const band = comparison.band || 'unknown';
  const changed = effective.overrideCount > 0 && effective.adjusted !== effective.auto;

  return (
    <div className="mso-header">
      <div className="mso-header-main">
        <div className={`mso-score ${band}`}>
          <span className="mso-score-value">
            {changed ? effective.adjusted : (comparison.coverage_percent ?? 0)}%
          </span>
          {changed && (
            <span className="mso-score-was">was {effective.auto}%</span>
          )}
        </div>
        <div className="mso-header-text">
          <div className="mso-headline">
            {effective.findings === 0
              ? 'No discrepancies'
              : `${effective.findings} finding${effective.findings === 1 ? '' : 's'}`}
            {effective.overrideCount > 0 && (
              <span className="mso-override-badge">
                {effective.overrideCount} override{effective.overrideCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="mso-submeta">
            <StatusPill status={status} />
            <span>{comparison.matched_count} of {comparison.matched_count + (comparison.unmatched_items?.length || 0)} sites verified</span>
            <span>·</span>
            <span>{formatDate(comparison.generated_at)}</span>
          </div>
        </div>
        {canRegenerate && (
          <button className="mso-btn" onClick={onRegenerate} disabled={regenerating}>
            {regenerating ? 'Running…' : 'Re-run'}
          </button>
        )}
      </div>

      {signoff && (
        <div className="mso-signed-strip">
          <strong>{signoff.decision === 'approved' ? 'Approved' : 'Rejected'}</strong>
          {' by '}{signoff.reviewed_by}{' · '}{formatDate(signoff.reviewed_at)}
          {signoff.comments && <div className="mso-signed-comment">{signoff.comments}</div>}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ready: { label: 'Ready for review', cls: 'ready' },
    approved: { label: 'Approved', cls: 'ok' },
    rejected: { label: 'Rejected', cls: 'bad' },
    pending: { label: 'Pending', cls: 'muted' },
  };
  const s = map[status] || { label: status, cls: 'muted' };
  return <span className={`mso-pill ${s.cls}`}>{s.label}</span>;
}

function Diagnostics({ diagnostics }) {
  const [open, setOpen] = useState(false);
  if (!diagnostics) return null;
  const unresolved = diagnostics.panels_unresolved || 0;

  return (
    <div className="mso-diag">
      <button className="mso-diag-toggle" onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} {diagnostics.screenshots_analyzed} screenshots ·{' '}
        {diagnostics.panels_total} panels
        {unresolved > 0 && (
          <span className="mso-diag-warn"> · {unresolved} unattributed</span>
        )}
      </button>
      {open && (
        <div className="mso-diag-body">
          {unresolved > 0 && (
            <p className="mso-diag-note">
              Panels that could not be matched to a modality are excluded from scoring
              rather than counted as missing.
            </p>
          )}
          <dl>
            <dt>USMON rows</dt><dd>{diagnostics.usmon_rows ?? '—'}</dd>
            <dt>Screen Archive</dt><dd className="mono">{shortPath(diagnostics.screenArchivePath)}</dd>
            <dt>Labels filtered</dt><dd>{diagnostics.suppressed_labels ?? '—'}</dd>
          </dl>
          {diagnostics.unresolved_titles && Object.keys(diagnostics.unresolved_titles).length > 0 && (
            <div className="mso-unresolved">
              {Object.entries(diagnostics.unresolved_titles).map(([title, n]) => (
                <span key={title} className="mso-chip">{title} ×{n}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- modality group */

function ModalityGroup({ group, open, onToggle, overrides, onOverride, onZoom, editable }) {
  const items = [...(group.unmatched_items || []), ...(group.matched_items || [])];
  const reportOnly = group.report_only_items || [];
  const adjusted = items.filter(it => {
    const v = overrides[it.id]?.verdict;
    if (v === 'na') return false;
    return (v || it.match_status) !== 'matched';
  }).length;

  return (
    <section className={`mso-group ${adjusted > 0 ? 'has-findings' : 'clean'}`}>
      <button className="mso-group-head" onClick={onToggle}>
        <span className="mso-caret">{open ? '▾' : '▸'}</span>
        <span className="mso-group-name">{group.modality}</span>
        {group.cpt_codes?.length > 0 && (
          <span className="mso-cpt">{group.cpt_codes.join(' · ')}</span>
        )}
        <span className="mso-group-spacer" />
        {group.total_count === 0 && reportOnly.length > 0 ? (
          <span className="mso-group-count">In Tech Report only</span>
        ) : adjusted > 0 ? (
          <span className="mso-group-count bad">{adjusted} to review</span>
        ) : group.advisory_count > 0 ? (
          <span className="mso-group-count">{group.advisory_count} not scorable</span>
        ) : (
          <span className="mso-group-count ok">All verified</span>
        )}
        <span className={`mso-group-score ${group.band || 'unknown'}`}>
          {group.matched_count}/{group.matched_count + (group.discrepancy_count || 0)}
        </span>
      </button>

      {open && (
        <div className="mso-group-body">
          <UsmonPanel group={group} />
          <div className="mso-items">
            {items.map(item => (
              <CheckRow
                key={item.id}
                item={item}
                override={overrides[item.id]}
                onOverride={patch => onOverride(item.id, patch)}
                onZoom={onZoom}
                editable={editable}
              />
            ))}
          </div>
          <ReportOnlyList items={reportOnly} />
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ times */

const TIME_STATES = {
  match:         { label: 'Agrees',       cls: 'ok'   },
  drift:         { label: 'Differs',      cls: 'warn' },
  missing_chat:  { label: 'Not in chat',  cls: 'na'   },
  missing_usmon: { label: 'Not in USMON', cls: 'na'   },
};

function clockOf(iso) {
  if (!iso) return '—';
  const t = String(iso).split('T')[1];
  return t ? t.slice(0, 5) : String(iso);
}

function deltaLabel(mins) {
  if (mins == null) return '—';
  if (mins === 0) return 'same';
  const a = Math.abs(mins);
  return (mins > 0 ? '+' : '−') + a + (a === 1 ? ' min' : ' mins');
}

/**
 * Chat times against USMON, one row per event.
 *
 * Kept out of the modality groups on purpose. A site check asks whether
 * something is present in both places; a time check asks how far apart two
 * clocks are, which needs three columns and a signed difference. Folding these
 * into mso-items would also change what a group's matched/total count means.
 *
 * Scored on its own, separate from the site comparison, so a case can be clean
 * on sites and still show a time problem.
 */

/**
 * Exact match only: any difference at all is a miss.
 *
 * Chat times are expected on every case, so an event USMON recorded and the
 * chat didn't counts against the score. The reverse doesn't — when USMON has
 * no time there is nothing to compare, and the row leaves the denominator
 * rather than scoring zero against the tech.
 */
function scoreTimes(checks) {
  const scored = checks.filter(c => c.status !== 'missing_usmon');
  if (scored.length === 0) return null;
  const earned = scored.filter(c => c.status === 'match').length;
  const pct = (earned / scored.length) * 100;
  return {
    earned,
    total: scored.length,
    pct: Math.round(pct * 10) / 10,
    band: pct >= 95 ? 'ok' : pct >= 80 ? 'warn' : 'bad',
  };
}
function TimeChecks({ checks }) {
  // Renders even with nothing to show. Hiding the section entirely made a
  // working feature look identical to a broken one.
  if (!checks || checks.length === 0) {
    return (
      <section className="mso-times">
        <SectionHead title="Times" sub="Chat log against USMON" />
        <p className="mso-empty">
          No times have been read from the chat log for this case.
        </p>
      </section>
    );
  }

  const s = scoreTimes(checks);
  const missed = s ? s.total - s.earned : 0;

  return (
    <section className="mso-times">
      <SectionHead
        title="Times"
        sub="Chat log against USMON"
        count={!s ? 'Nothing to compare'
          : missed === 0 ? `All ${s.total} match` : `${missed} of ${s.total} off`}
        score={s ? s.pct : null}
        band={s ? s.band : null}
      />

      <div className="mso-times-grid">
        <div className="mso-times-row head">
          <span>Event</span>
          <span>Chat</span>
          <span>USMON</span>
          <span>Difference</span>
        </div>

        {checks.map(c => {
          const s = TIME_STATES[c.status] || TIME_STATES.missing_usmon;
          const scored = c.status === 'match' || c.status === 'drift';
          return (
            <div key={c.id} className={`mso-times-row ${s.cls}`}>
              <span className="mso-times-event">{c.label}</span>
              <span className="mso-times-clock">{clockOf(c.chat_time)}</span>
              <span className="mso-times-clock">{clockOf(c.usmon_time)}</span>
              <span className="mso-times-delta"
                title={c.matched_text
                  ? `Read from: ${c.matched_text}`
                  : 'No matching line found in the chat log'}>
                {scored ? deltaLabel(c.delta_minutes) : s.label}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- sections */

/**
 * Shared header for the two comparison sections, so Modalities and Times read
 * as siblings rather than one list and one afterthought.
 */
function SectionHead({ title, sub, count, score, band }) {
  return (
    <div className="mso-section-head">
      <h3 className="mso-section-title">{title}</h3>
      <span className="mso-section-sub">{sub}</span>
      <span className="mso-section-spacer" />
      {count && <span className="mso-section-count">{count}</span>}
      {score != null && (
        <span className={`mso-section-score ${band || ''}`}>{score}%</span>
      )}
    </div>
  );
}

/* The TOF entries exactly as the tech recorded them. Shown as lines rather
 * than summarised, because a reviewer reading a train of four wants the times
 * and the counts in order so they can see the recovery for themselves. An
 * unparseable time is printed as typed rather than blanked, so it is obvious
 * something was entered. */
function To4Readings({ readings }) {
  return (
    <div className="mso-to4">
      <div className="mso-to4-head">
        <span className="mso-label">Recorded readings</span>
        <span className="mso-chip subtle">
          {readings.length} {readings.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
      <ol className="mso-to4-list">
        {readings.map((r, i) => (
          <li key={i} className="mso-to4-row">
            <span className="mso-to4-seq">{r.seq ?? i + 1}</span>
            <span className={`mso-to4-time${r.time_missing ? ' missing' : ''}`}>
              {r.time || 'no time'}
            </span>
            <span className="mso-to4-count">
              {r.response == null ? 'blank' : `${r.response} of 4`}
            </span>
            <span className="mso-to4-rel">{r.reliability || ''}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function UsmonPanel({ group }) {
  const sites = group.usmon_sites || [];
  const hasSites = sites.length > 0;
  const readings = group.to4_readings || [];
  const hasReadings = readings.length > 0;

  if (group.in_usmon === false && !hasSites && !hasReadings) {
    return (
      <div className="mso-techreport">
        <div className="mso-techreport-head">
          <span className="mso-label">USMON records</span>
        </div>
        <p className="mso-empty">Not recorded in USMON for this case.</p>
      </div>
    );
  }

  return (
    <div className="mso-techreport">
      <div className="mso-techreport-head">
        <span className="mso-label">USMON records</span>
        {group.lateralities?.length > 0 && (
          <span className="mso-chip subtle">
            {group.lateralities.map(l => l.toLowerCase()).join(' + ')}
          </span>
        )}
        {group.panel_count > 0 && (
          <span className="mso-chip subtle">{group.panel_count} panels</span>
        )}
      </div>
      {hasSites && (
        <div className="mso-site-chips">
          {sites.map((s, i) => <span key={i} className="mso-site-chip">{s}</span>)}
        </div>
      )}
      {hasReadings && <To4Readings readings={readings} />}
      {!hasSites && !hasReadings && (
        <p className="mso-empty">
          No body sites recorded for this modality — only its presence is checked.
        </p>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- one check */

function CheckRow({ item, override, onOverride, onZoom, editable }) {
  const [openOv, setOpenOv] = useState(false);
  const effective = override?.verdict === 'na'
    ? null
    : (override?.verdict || item.match_status);
  const v = effective ? (VERDICTS[effective] || VERDICTS.unmatched) : null;
  const isOverridden = !!override?.verdict;
  const images = item.evidence?.images || [];

  return (
    <div className={`mso-row ${v ? v.cls : 'na'} ${isOverridden ? 'overridden' : ''}`}>
      <span className={`mso-row-icon ${v ? v.cls : 'na'}`}>{v ? v.icon : '–'}</span>

      <div className="mso-row-main">
        <div className="mso-row-title">
          <span className="mso-site">{item.value}</span>
          <span className="mso-type">{item.event_type}</span>
          {item.timestamp && <span className="mso-lat">{item.timestamp}</span>}
          {item.confidence === 'ADVISORY' && (
            <span className="mso-chip subtle"
              title="Cannot be verified from screenshots; excluded from scoring">
              not scorable
            </span>
          )}
          {item.in_usmon === false && (
            <span className="mso-chip subtle" title="Seen on screen, absent from USMON">
              not in USMON
            </span>
          )}
          <ReportChip item={item} />
          {isOverridden && (
            <span className="mso-chip override">
              overridden → {VERDICTS[override.verdict]?.label || 'N/A'}
            </span>
          )}
        </div>

        <div className="mso-row-detail">
          {item.match_detail}
          {isOverridden && (
            <span className="mso-was"> · system said {VERDICTS[item.match_status]?.label}</span>
          )}
        </div>

        {item.screen_ref && item.screen_ref !== item.value && (
          <div className="mso-row-ref">
            Screenshot: <code>{item.screen_ref}</code>
          </div>
        )}

        {item.tech_report_ref && item.tech_report_ref !== item.value && (
          <div className="mso-row-ref">
            Tech Report: <code>{item.tech_report_ref}</code>
          </div>
        )}

        {override?.note && <div className="mso-row-note">“{override.note}”</div>}

        {images.length > 0 && (
          <div className="mso-evidence-links">
            {images.slice(0, 4).map(img => (
              <button key={img.image} className="mso-ev-link" onClick={() => onZoom({ item, img })}>
                Page {img.page}
                {img.panel_title ? ` · ${img.panel_title}` : ''}
              </button>
            ))}
            {images.length > 4 && <span className="mso-more">+{images.length - 4} more</span>}
          </div>
        )}
      </div>

      {editable && (
        <div className="mso-row-actions">
          <button className="mso-ov-toggle" onClick={() => setOpenOv(!openOv)}>
            {isOverridden ? 'Edit' : 'Override'}
          </button>
          {openOv && (
            <div className="mso-ov-panel">
              <select
                value={override?.verdict || ''}
                onChange={e => onOverride({ verdict: e.target.value })}
              >
                {OVERRIDE_CHOICES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Reason (optional)"
                value={override?.note || ''}
                onChange={e => onOverride({ note: e.target.value })}
              />
              <button className="mso-ov-done" onClick={() => setOpenOv(false)}>Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- tech report */

/**
 * The Tech Report is shown for review only. Nothing here feeds a score, a
 * finding count or the sign-off summary, which all still come from USMON
 * against the screenshots.
 */

const REPORT_STATES = {
  listed: { label: 'in Tech Report', title: 'Listed in the Tech Report' },
  listed_unassigned: { label: 'in Tech Report',
    title: 'Listed in the Tech Report, in a section whose modality could not be read' },
  listed_other_modality: { label: 'in Tech Report, other modality',
    title: 'Listed in the Tech Report under a different modality' },
  not_listed: { label: 'not in Tech Report',
    title: 'The Tech Report lists this modality but not this site' },
};

const REPORT_STATUS_TEXT = {
  no_report: 'No Tech Report has been processed for this case.',
  pdf_missing: 'The Tech Report file could not be found.',
  no_text_layer: 'The Tech Report is a scanned image, so its site list could not be read.',
  error: 'The Tech Report could not be read.',
  disabled: 'The Tech Report check is turned off.',
};

function ReportChip({ item }) {
  const s = REPORT_STATES[item.report_state];
  if (!s) return null;
  return (
    <span className="mso-chip subtle" title={`${s.title}. For review only, not scored.`}>
      {s.label}
    </span>
  );
}

function ReportNote({ report }) {
  // Runs from before the report check carry no report field at all.
  if (!report || report.status === 'not_checked') return null;
  if (report.status !== 'found') {
    return (
      <p className="mso-empty">
        Tech Report not available. {REPORT_STATUS_TEXT[report.status] || ''}
      </p>
    );
  }
  const n = report.counts || {};
  const listed = n.listed || 0;
  return (
    <p className="mso-empty">
      Tech Report: {listed} site{listed === 1 ? '' : 's'} listed,
      {' '}{n.not_listed || 0} not listed, {n.report_only || 0} only in the report.
      {' '}For review only, not scored.
    </p>
  );
}

function ReportOnlyList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mso-items">
      <div className="mso-techreport-head">
        <span className="mso-label">In the Tech Report only</span>
      </div>
      {items.map(item => (
        <div key={item.id} className="mso-row na">
          <span className="mso-row-icon na">–</span>
          <div className="mso-row-main">
            <div className="mso-row-title">
              <span className="mso-site">{item.value}</span>
              <span className="mso-chip subtle"
                title="Listed in the Tech Report, not found in USMON or the screenshots">
                not scored
              </span>
            </div>
            <div className="mso-row-detail">{item.match_detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- lightbox */

function Lightbox({ item, onClose }) {
  const { img } = item;
  return (
    <div className="mso-lightbox" onClick={onClose}>
      <div className="mso-lightbox-inner" onClick={e => e.stopPropagation()}>
        <div className="mso-lightbox-head">
          <span>Page {img.page}{img.panel_title ? ` · ${img.panel_title}` : ''}</span>
          <button className="mso-close" onClick={onClose}>×</button>
        </div>
        {img.url ? (
          <img src={img.url} alt={`Screenshot page ${img.page}`} />
        ) : (
          <p className="mso-empty">Image not available. Re-run the comparison to cache screenshots.</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ signoff panel */

function SignoffPanel({ comments, setComments, effective, onApprove, onReject, pending }) {
  return (
    <div className="mso-signoff">
      <div className="mso-signoff-summary">
        {effective.findings === 0
          ? 'All sites verified.'
          : `${effective.findings} site${effective.findings === 1 ? '' : 's'} unresolved.`}
        {effective.overrideCount > 0 &&
          ` ${effective.overrideCount} manual override${effective.overrideCount === 1 ? '' : 's'} applied.`}
      </div>
      <textarea
        className="mso-comments"
        placeholder="Notes for the record (optional)"
        value={comments}
        onChange={e => setComments(e.target.value)}
        rows={3}
      />
      <div className="mso-signoff-actions">
        <button className="mso-btn danger" onClick={onReject} disabled={pending}>
          Reject
        </button>
        <button className="mso-btn primary" onClick={onApprove} disabled={pending}>
          {pending ? 'Saving…' : 'Approve'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- utils */

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function shortPath(p) {
  if (!p) return '—';
  const parts = String(p).split('/');
  return parts[parts.length - 1];
}